"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FilterChipWithMenu = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _identifier = require("../../util/identifier");
var _Select = require("../Select/Select");
var _VisuallyHidden = require("../VisuallyHidden/VisuallyHidden");
var _FilterChipWithMenu = require("./FilterChipWithMenu.styles");
var _excluded = ["children", "chipElevation", "defaultValue", "disabled", "error", "fluid", "icon", "labelVoiceOver", "onChange", "required", "size", "value"];
/**
 * Filter chips with menus behave similar to select fields. They're used mainly
 * for filtering and sorting content (ex: search filters).
 *
 * @extends Select
 * @related ['AssistChip', 'InputChip']
 * @since 8.97.0
 */
var FilterChipWithMenu = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var children = props.children,
    chipElevation = props.chipElevation,
    defaultValue = props.defaultValue,
    disabled = props.disabled,
    error = props.error,
    fluid = props.fluid,
    icon = props.icon,
    labelVoiceOver = props.labelVoiceOver,
    onChange = props.onChange,
    required = props.required,
    size = props.size,
    value = props.value,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var _useState = (0, _react.useState)(!!props.value || !!props.defaultValue),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    isToggledOn = _useState2[0],
    setIsToggledOn = _useState2[1];

  // Generate an id to link label to the select
  var selectName = (0, _react.useRef)((0, _identifier.identifier)()).current;
  var changeCallback = (0, _react.useCallback)(function (e) {
    setIsToggledOn(!!e.target.value);
    if (onChange) {
      onChange(e);
    }
  }, [onChange]);
  return /*#__PURE__*/_react.default.createElement(_FilterChipWithMenu.StyledFilterChipWithMenu, (0, _extends2.default)({
    chipElevation: chipElevation,
    disabled: disabled,
    fluid: false,
    icon: icon,
    isToggledOn: isToggledOn,
    ref: forwardedRef,
    htmlFor: selectName
  }, rest), /*#__PURE__*/_react.default.createElement(_VisuallyHidden.VisuallyHidden, null, labelVoiceOver), icon, /*#__PURE__*/_react.default.createElement(_Select.Select, {
    id: selectName,
    defaultValue: defaultValue,
    disabled: disabled,
    onChange: changeCallback,
    value: value
  }, children));
});
exports.FilterChipWithMenu = FilterChipWithMenu;
FilterChipWithMenu.displayName = 'FilterChipWithMenu';
FilterChipWithMenu.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Accepts `<option>` and `<optgroup>` nodes.
   *
   * Note that the first `option` element must have a value of empty string
   * (ex: `<option value="">`) if it's acting as the label for the chip. This is how
   * the chip knows whether to display in its default state (no value selected) vs its
   * "selected" state (value selected).
   */
  children: _propTypes.default.node.isRequired,
  /**
   * When true, adds a drop shadow. Useful for making the chip easier to see on
   * certain backgrounds (ex: on top of a photo or map)
   */
  chipElevation: _propTypes.default.bool,
  /**
   * The value of the default, selected option when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultValue: _propTypes.default.string,
  /**
   * Sets the chip as disabled.
   */
  disabled: _propTypes.default.bool,
  /**
   * Adds a leading icon. Takes an `Icon` component but ignores its `size` prop
   * (i.e. the icon size is fixed). Does not support `DetailedIcon`.
   */
  icon: _propTypes.default.node,
  /**
   * The label that will be announced to screen readers when you focus on the
   * chip or change your selection. Rendered as visually hidden text.
   *
   * Defaults to announcing "filter". The text should reflect the type of action this
   * chip provides. Do not include the value of the selected menu option since
   * assistive technologies will usually announce this anyway.
   */
  labelVoiceOver: _propTypes.default.string,
  /**
   * A callback that is triggered when a different option is selected. Passes the event object.
   */
  onChange: _propTypes.default.func,
  /**
   * The value of the selected option when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  value: _propTypes.default.string
} : {};
FilterChipWithMenu.defaultProps = {
  chipElevation: false,
  disabled: false,
  icon: null,
  labelVoiceOver: 'Filter'
};